import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import SuperAdminLayout from '@/components/admin/super-admin-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, subDays, subMonths } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { apiRequest } from '@/lib/queryClient';
import {
  Users,
  Building,
  Package,
  CreditCard,
  Activity,
  TrendingUp,
  BarChartIcon,
  PieChartIcon,
  LineChartIcon,
  Calendar,
  DownloadIcon,
  RefreshCw
} from 'lucide-react';

// Define types
interface AnalyticsSummary {
  totalUsers: number;
  totalTenants: number;
  totalSubscriptions: number;
  totalRevenue: number;
  activeUsers: number;
  activeTenants: number;
  growthRate: number;
  conversionRate: number;
}

interface UserActivityData {
  date: string;
  logins: number;
  signups: number;
  activeUsers: number;
}

interface RevenueData {
  date: string;
  revenue: number;
  subscriptions: number;
}

interface TenantData {
  date: string;
  newTenants: number;
  activeTenants: number;
  churnedTenants: number;
}

interface SubscriptionData {
  plan: string;
  count: number;
  value: number;
}

// Default values
const defaultSummary: AnalyticsSummary = {
  totalUsers: 0,
  totalTenants: 0,
  totalSubscriptions: 0,
  totalRevenue: 0,
  activeUsers: 0,
  activeTenants: 0,
  growthRate: 0,
  conversionRate: 0
};

// Colors for charts
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const AnalyticsPage = () => {
  const [dateRange, setDateRange] = useState({
    from: subMonths(new Date(), 1),
    to: new Date(),
  });
  const [chartType, setChartType] = useState('bar');
  const [dataType, setDataType] = useState('users');

  // Fetch analytics summary
  const { data: summary = defaultSummary, isLoading: summaryLoading } = useQuery<AnalyticsSummary>({
    queryKey: ['/api/admin/system/analytics/summary', { from: dateRange.from, to: dateRange.to }],
    queryFn: async () => {
      try {
        const fromDateStr = format(dateRange.from, 'yyyy-MM-dd');
        const toDateStr = format(dateRange.to, 'yyyy-MM-dd');
        const response = await apiRequest(`/api/admin/system/analytics/summary?fromDate=${fromDateStr}&toDate=${toDateStr}`, {
          method: 'GET'
        });
        console.log('Summary response:', response);
        // The apiRequest automatically handles JSON parsing
        return response || defaultSummary;
      } catch (error) {
        console.error('API error:', error);
        return defaultSummary;
      }
    },
  });

  // Fetch user activity data
  const { data: userActivityData = [], isLoading: userDataLoading } = useQuery<UserActivityData[]>({
    queryKey: ['/api/admin/system/user-activity', { from: dateRange.from, to: dateRange.to }],
    queryFn: async () => {
      try {
        const fromDateStr = format(dateRange.from, 'yyyy-MM-dd');
        const toDateStr = format(dateRange.to, 'yyyy-MM-dd');
        const response = await apiRequest(`/api/admin/system/analytics/user-activity?fromDate=${fromDateStr}&toDate=${toDateStr}`, {
          method: 'GET'
        });
        console.log('User activity response:', response);
        // The apiRequest automatically handles JSON parsing
        return response || [];
      } catch (error) {
        console.error('API error:', error);
        return [];
      }
    },
  });

  // Fetch revenue data
  const { data: revenueData = [], isLoading: revenueDataLoading } = useQuery<RevenueData[]>({
    queryKey: ['/api/admin/system/analytics/revenue', { from: dateRange.from, to: dateRange.to }],
    queryFn: async () => {
      try {
        const fromDateStr = format(dateRange.from, 'yyyy-MM-dd');
        const toDateStr = format(dateRange.to, 'yyyy-MM-dd');
        const response = await apiRequest(`/api/admin/system/analytics/revenue?fromDate=${fromDateStr}&toDate=${toDateStr}`, {
          method: 'GET'
        });
        console.log('Revenue data response:', response);
        return response || [];
      } catch (error) {
        console.error('API error:', error);
        return [];
      }
    },
  });

  // Fetch tenant data
  const { data: tenantData = [], isLoading: tenantDataLoading } = useQuery<TenantData[]>({
    queryKey: ['/api/admin/system/analytics/tenants', { from: dateRange.from, to: dateRange.to }],
    queryFn: async () => {
      try {
        const fromDateStr = format(dateRange.from, 'yyyy-MM-dd');
        const toDateStr = format(dateRange.to, 'yyyy-MM-dd');
        const response = await apiRequest(`/api/admin/system/analytics/tenants?fromDate=${fromDateStr}&toDate=${toDateStr}`, {
          method: 'GET'
        });
        console.log('Tenant data response:', response);
        return response || [];
      } catch (error) {
        console.error('API error:', error);
        return [];
      }
    },
  });

  // Fetch subscription data
  const { data: subscriptionData = [], isLoading: subscriptionDataLoading } = useQuery<SubscriptionData[]>({
    queryKey: ['/api/admin/system/analytics/subscriptions'],
    queryFn: async () => {
      try {
        const response = await apiRequest('/api/admin/system/analytics/subscriptions', { 
          method: 'GET'
        });
        console.log('Subscription data response:', response);
        return response || [];
      } catch (error) {
        console.error('API error:', error);
        return [];
      }
    },
  });

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  // Predefined date ranges
  const handleLastWeek = () => {
    setDateRange({
      from: subDays(new Date(), 7),
      to: new Date()
    });
  };
  
  const handleLastMonth = () => {
    setDateRange({
      from: subMonths(new Date(), 1),
      to: new Date()
    });
  };
  
  const handleLast3Months = () => {
    setDateRange({
      from: subMonths(new Date(), 3),
      to: new Date()
    });
  };
  
  const handleYearToDate = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    setDateRange({
      from: startOfYear,
      to: now
    });
  };

  // Determine which data to show based on dataType selection
  const getChartData = () => {
    switch (dataType) {
      case 'users':
        return userActivityData;
      case 'revenue':
        return revenueData;
      case 'tenants':
        return tenantData;
      case 'subscriptions':
        return subscriptionData;
      default:
        return userActivityData;
    }
  };

  // Render appropriate chart based on chartType and dataType
  const renderChart = () => {
    const data = getChartData();
    const isLoading = userDataLoading || revenueDataLoading || tenantDataLoading || subscriptionDataLoading;

    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-80">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="flex flex-col justify-center items-center h-80">
          <BarChartIcon className="h-10 w-10 text-muted-foreground opacity-50 mb-2" />
          <p className="text-muted-foreground">No data available for the selected time period</p>
        </div>
      );
    }

    if (dataType === 'subscriptions') {
      // For subscription data, always use pie chart
      return (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={true}
              outerRadius={150}
              fill="#8884d8"
              dataKey="value"
              nameKey="plan"
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => formatCurrency(value as number)} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    // For other data types, render based on chartType
    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              {dataType === 'users' && (
                <>
                  <Bar dataKey="logins" fill="#8884d8" name="Logins" />
                  <Bar dataKey="signups" fill="#82ca9d" name="Signups" />
                  <Bar dataKey="activeUsers" fill="#ffc658" name="Active Users" />
                </>
              )}
              {dataType === 'revenue' && (
                <>
                  <Bar dataKey="revenue" fill="#8884d8" name="Revenue ($)" />
                  <Bar dataKey="subscriptions" fill="#82ca9d" name="Subscriptions" />
                </>
              )}
              {dataType === 'tenants' && (
                <>
                  <Bar dataKey="newTenants" fill="#8884d8" name="New Tenants" />
                  <Bar dataKey="activeTenants" fill="#82ca9d" name="Active Tenants" />
                  <Bar dataKey="churnedTenants" fill="#ff8042" name="Churned Tenants" />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              {dataType === 'users' && (
                <>
                  <Line type="monotone" dataKey="logins" stroke="#8884d8" name="Logins" />
                  <Line type="monotone" dataKey="signups" stroke="#82ca9d" name="Signups" />
                  <Line type="monotone" dataKey="activeUsers" stroke="#ffc658" name="Active Users" />
                </>
              )}
              {dataType === 'revenue' && (
                <>
                  <Line type="monotone" dataKey="revenue" stroke="#8884d8" name="Revenue ($)" />
                  <Line type="monotone" dataKey="subscriptions" stroke="#82ca9d" name="Subscriptions" />
                </>
              )}
              {dataType === 'tenants' && (
                <>
                  <Line type="monotone" dataKey="newTenants" stroke="#8884d8" name="New Tenants" />
                  <Line type="monotone" dataKey="activeTenants" stroke="#82ca9d" name="Active Tenants" />
                  <Line type="monotone" dataKey="churnedTenants" stroke="#ff8042" name="Churned Tenants" />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        // Create aggregated data for pie chart
        type PieDataItem = { name: string; value: number };
        let pieData: PieDataItem[] = [];
        
        if (dataType === 'users' && Array.isArray(data)) {
          const userData = data as UserActivityData[];
          const totalLogins = userData.reduce((sum, item) => sum + (item.logins || 0), 0);
          const totalSignups = userData.reduce((sum, item) => sum + (item.signups || 0), 0);
          const totalActiveUsers = userData.reduce((sum, item) => sum + (item.activeUsers || 0), 0);
          pieData = [
            { name: 'Logins', value: totalLogins },
            { name: 'Signups', value: totalSignups },
            { name: 'Active Users', value: totalActiveUsers },
          ];
        } else if (dataType === 'revenue' && Array.isArray(data)) {
          const revenueData = data as RevenueData[];
          const totalRevenue = revenueData.reduce((sum, item) => sum + (item.revenue || 0), 0);
          const totalSubscriptions = revenueData.reduce((sum, item) => sum + (item.subscriptions || 0), 0);
          pieData = [
            { name: 'Revenue', value: totalRevenue },
            { name: 'Subscriptions', value: totalSubscriptions },
          ];
        } else if (dataType === 'tenants' && Array.isArray(data)) {
          const tenantData = data as TenantData[];
          const totalNewTenants = tenantData.reduce((sum, item) => sum + (item.newTenants || 0), 0);
          const totalActiveTenants = tenantData.reduce((sum, item) => sum + (item.activeTenants || 0), 0);
          const totalChurnedTenants = tenantData.reduce((sum, item) => sum + (item.churnedTenants || 0), 0);
          pieData = [
            { name: 'New Tenants', value: totalNewTenants },
            { name: 'Active Tenants', value: totalActiveTenants },
            { name: 'Churned Tenants', value: totalChurnedTenants },
          ];
        }

        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={true}
                outerRadius={150}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                label={({ name, percent }: { name: string; percent: number }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  const isLoading = summaryLoading || userDataLoading || revenueDataLoading || tenantDataLoading || subscriptionDataLoading;

  return (
    <div className="min-h-screen bg-slate-50">
      <SuperAdminLayout>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
            <p className="text-slate-500">System-wide analytics and performance metrics</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex gap-2">
              <Button size="sm" variant="outline" onClick={handleLastWeek}>
                7 days
              </Button>
              <Button size="sm" variant="outline" onClick={handleLastMonth}>
                30 days
              </Button>
              <Button size="sm" variant="outline" onClick={handleLast3Months}>
                3 months
              </Button>
              <Button size="sm" variant="outline" onClick={handleYearToDate}>
                Year to date
              </Button>
            </div>
            <DatePickerWithRange
              date={{
                from: dateRange.from,
                to: dateRange.to
              }}
              onDateChange={(range) => {
                if (range.from && range.to) {
                  setDateRange({
                    from: range.from,
                    to: range.to
                  });
                }
              }}
            />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Users className="h-5 w-5 text-muted-foreground mr-2" />
                <div className="text-2xl font-bold">
                  {isLoading ? <span className="animate-pulse">...</span> : summary.totalUsers}
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {summary.activeUsers} active
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Tenants
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Building className="h-5 w-5 text-muted-foreground mr-2" />
                <div className="text-2xl font-bold">
                  {isLoading ? <span className="animate-pulse">...</span> : summary.totalTenants}
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {summary.activeTenants} active
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Package className="h-5 w-5 text-muted-foreground mr-2" />
                <div className="text-2xl font-bold">
                  {isLoading ? <span className="animate-pulse">...</span> : summary.totalSubscriptions}
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {summary.conversionRate}% conversion rate
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <CreditCard className="h-5 w-5 text-muted-foreground mr-2" />
                <div className="text-2xl font-bold">
                  {isLoading ? <span className="animate-pulse">...</span> : formatCurrency(summary.totalRevenue)}
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {summary.growthRate}% growth rate
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <Tabs value={dataType} onValueChange={setDataType} className="w-full sm:w-auto">
            <TabsList className="grid w-full sm:w-auto grid-cols-4">
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="revenue">Revenue</TabsTrigger>
              <TabsTrigger value="tenants">Tenants</TabsTrigger>
              <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex w-full sm:w-auto gap-2">
            {dataType !== 'subscriptions' && (
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Chart Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">
                    <div className="flex items-center">
                      <BarChartIcon className="h-4 w-4 mr-2" />
                      Bar Chart
                    </div>
                  </SelectItem>
                  <SelectItem value="line">
                    <div className="flex items-center">
                      <LineChartIcon className="h-4 w-4 mr-2" />
                      Line Chart
                    </div>
                  </SelectItem>
                  <SelectItem value="pie">
                    <div className="flex items-center">
                      <PieChartIcon className="h-4 w-4 mr-2" />
                      Pie Chart
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="icon">
              <DownloadIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Chart */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>
              {dataType === 'users' && 'User Activity'}
              {dataType === 'revenue' && 'Revenue Analytics'}
              {dataType === 'tenants' && 'Tenant Analytics'}
              {dataType === 'subscriptions' && 'Subscription Distribution'}
            </CardTitle>
            <CardDescription>
              {dataType === 'users' && 'Login, signup, and active user metrics'}
              {dataType === 'revenue' && 'Revenue and subscription metrics'}
              {dataType === 'tenants' && 'Tenant growth and activity metrics'}
              {dataType === 'subscriptions' && 'Distribution of subscriptions by plan'}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {renderChart()}
          </CardContent>
        </Card>
      </SuperAdminLayout>
    </div>
  );
};

export default AnalyticsPage;